/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GridLayout, { WidthProvider } from "react-grid-layout";

import useDashboardMetrics, { PERIODS, periodLabelFor } from "../CompanyMetrics/dashboards/useDashboardMetrics";
import WidgetCard from "../CompanyMetrics/dashboards/WidgetCard";
import ComponentCard from "../CompanyMetrics/dashboards/ComponentCard";
import "../CompanyMetrics/dashboards/dashboards.css";

const Grid = WidthProvider(GridLayout);

const FONT = `'DM Sans', 'Inter', system-ui, -apple-system, sans-serif`;

// Auto-scroll pacing for boards taller than the screen.
const SCROLL_SPEED = 20; // px per second — a slow, easy-to-read crawl
const END_HOLD = 5000; // ms paused at the bottom, and at the top between loops
const START_HOLD = 15000; // ms held on the first screen before it ever moves
const FADE_PX = 34; // soft edge that only appears once content scrolls past it

/**
 * One published dashboard, rendered as a TV scene.
 *
 * The board is drawn exactly as the builder draws it — same 12-column grid,
 * same cards, at full readable width. If it is taller than the screen it is
 * NOT shrunk (that made the cards unreadable); instead it crawls slowly from
 * top to bottom on a loop, pausing at each end, so a wall TV shows the whole
 * board over time without ever squashing it.
 *
 * When the remote/keyboard focus is on the board (`navActive`), the auto-crawl
 * pauses and the parent drives the scroll a step at a time via the imperative
 * `nudge(dir)` handle. When focus leaves, the crawl resumes from where it sat.
 */
const TVDashboardScene = forwardRef(({ dashboard, dark, workspaceName, navActive = false }, ref) => {
  const metricsApi = useDashboardMetrics();
  const { getSeries, getSummary, getMetric, setPeriod, setCustomRange } = metricsApi;

  /* The saved window, as the board's own cards need to read it. A custom board
     carries its days too — without them every card silently fell back to
     month-to-date while the header still said "Custom range". */
  const boardWindow = PERIODS.some((p) => p.id === dashboard.dateRange?.preset)
    ? dashboard.dateRange.preset
    : "month";
  const boardCustom = boardWindow === "custom"
    ? { from: dashboard.dateRange?.from || "", to: dashboard.dateRange?.to || "" }
    : null;
  const customFrom = boardCustom?.from || "";
  const customTo = boardCustom?.to || "";

  // The board plays with the period it was saved with.
  useEffect(() => {
    setPeriod(boardWindow);
    if (boardWindow === "custom") setCustomRange({ from: customFrom, to: customTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard._id, boardWindow, customFrom, customTo]);

  const widgets = useMemo(
    () =>
      (dashboard.widgets || []).map((w, idx) => ({
        ...w,
        i: w._id || `tvw${idx}`,
      })),
    [dashboard.widgets]
  );

  /* What else is on this board, for the one card that reads another: Revenue,
     Expenses & Labor takes its Labor figure from a Payroll vs Revenue card
     sitting beside it, so on a TV wall it needs the same sibling list the
     builder threads through boardCtx — without it the card would read
     "no payroll card on this dashboard" on the wall only. */
  const boardWidgets = useMemo(
    () => widgets
      .filter((w) => w.kind === "component" && w.comp)
      .map((w) => ({
        id: w.i,
        comp: w.comp,
        title: w.title || w.options?.title || "",
        options: w.options || {},
      })),
    [widgets]
  );

  // Gentle auto-scroll: crawl the board top-to-bottom whenever it overflows the
  // frame. Overflow is re-read every frame, so it stays correct across resizes
  // and late-loading cards. Honors reduced-motion by holding at the top.
  const stageRef = useRef(null);
  const boardRef = useRef(null);
  // Shared scroll state so the auto-crawl and the manual (remote) control both
  // read/write ONE position. yRef = current offset, maxRef = live overflow,
  // manualRef = remote has taken over (auto paused).
  const yRef = useRef(0);
  const maxRef = useRef(0);
  const manualRef = useRef(false);

  const paint = (y, overflow) => {
    const board = boardRef.current;
    const stage = stageRef.current;
    if (!board || !stage) return;
    board.style.transform = `translateY(${-y}px)`;
    // The soft edges only show where content is actually cut off: no top fade
    // while parked at the top, no bottom fade while parked at the bottom.
    stage.style.setProperty("--tv-fade-top", `${Math.min(y, FADE_PX)}px`);
    stage.style.setProperty("--tv-fade-bot", `${Math.min(overflow - y, FADE_PX)}px`);
  };

  // Remote/keyboard focus on the board pauses the crawl and drives it by step.
  useEffect(() => { manualRef.current = !!navActive; }, [navActive]);

  // One step of manual scroll (~18% of the frame per press), clamped.
  /* Live overflow straight from the DOM. The animation loop also tracks this,
     but the remote must never depend on that loop having run (a throttled tab
     would leave it at 0 and silently kill scrolling). */
  const overflowNow = () => {
    const stage = stageRef.current;
    const board = boardRef.current;
    if (!stage || !board) return 0;
    const max = Math.max(0, board.scrollHeight - stage.clientHeight);
    maxRef.current = max;
    return max;
  };

  useImperativeHandle(ref, () => ({
    nudge(dir) {
      const stage = stageRef.current;
      if (!stage) return;
      const max = overflowNow();
      const step = Math.max(80, Math.round(stage.clientHeight * 0.18));
      yRef.current = Math.max(0, Math.min(max, yRef.current + dir * step));
      paint(yRef.current, max);
    },
    /* Scroll just enough to bring a focused control fully into the frame —
       the board follows the remote's focus instead of being nudged blindly. */
    ensureVisible(el) {
      const stage = stageRef.current;
      if (!stage || !el || !el.getBoundingClientRect) return;
      const s = stage.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const pad = Math.round(s.height * 0.1);
      let dy = 0;
      if (r.top < s.top + pad) dy = r.top - (s.top + pad);
      else if (r.bottom > s.bottom - pad) dy = r.bottom - (s.bottom - pad);
      if (!dy) return;
      const max = overflowNow();
      yRef.current = Math.max(0, Math.min(max, yRef.current + dy));
      paint(yRef.current, max);
    },
    contains(el) {
      const board = boardRef.current;
      return !!(board && el && board.contains(el));
    },
    /* Is there still board left to reveal this way? The remote uses this to
       decide between "scroll the board" and "hand off to the top bar / tabs". */
    canScroll(dir) {
      const max = overflowNow();
      if (max <= 4) return false;
      return dir > 0 ? yRef.current < max - 2 : yRef.current > 2;
    },
    /* The visible frame, so the parent can tell what's actually on screen. */
    stageRect() {
      const stage = stageRef.current;
      return stage ? stage.getBoundingClientRect() : null;
    },
    root() {
      return boardRef.current;
    },
  }), []);

  useEffect(() => {
    const stage = stageRef.current;
    const board = boardRef.current;
    if (!stage || !board) return undefined;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let phase = "hold-top"; // hold-top → down → hold-bottom → up → hold-top
    let holdUntil = 0;
    let last = 0;
    let primed = false;

    const tick = (now) => {
      if (!primed) {
        last = now;
        // Longer hold the very first time so the top screen can be read
        // before the board ever starts moving.
        holdUntil = now + START_HOLD;
        primed = true;
      }
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const overflow = Math.max(0, board.scrollHeight - stage.clientHeight);
      maxRef.current = overflow;
      let y = yRef.current;

      if (overflow <= 8 || reduce) {
        y = 0;
      } else if (manualRef.current) {
        // Remote is driving — hold wherever the user left it (nudge sets yRef).
        y = Math.min(y, overflow);
        phase = "down"; // when focus leaves, resume by crawling downward
      } else if (phase === "hold-top") {
        if (now >= holdUntil) phase = "down";
      } else if (phase === "down") {
        y += SCROLL_SPEED * dt;
        if (y >= overflow) {
          y = overflow;
          phase = "hold-bottom";
          holdUntil = now + END_HOLD;
        }
      } else if (phase === "hold-bottom") {
        if (now >= holdUntil) phase = "up";
      } else if (phase === "up") {
        y -= SCROLL_SPEED * dt;
        if (y <= 0) {
          y = 0;
          phase = "hold-top";
          holdUntil = now + END_HOLD;
        }
      }
      if (y > overflow) y = overflow; // clamp if the frame grew mid-loop
      if (y < 0) y = 0;
      yRef.current = y;
      paint(y, overflow);
      raf = requestAnimationFrame(tick);
    };

    // Let the cards render and measure before the crawl starts.
    const t = setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, 500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
    // Depend only on the board id — the loop reads scrollHeight/clientHeight
    // live every frame, so it must NOT restart when metrics re-render (that
    // churns the `widgets` reference and would cancel the crawl mid-scroll).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard._id]);

  /* Title says the real window — "6 Sep – 12 Sep", not a bare "Custom range". */
  const periodTitle = (() => {
    const raw = periodLabelFor(boardWindow, boardCustom);
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
  })();

  /* Tight chrome on purpose. A plain two-row board comes out ~880px tall and a
     1080p TV frame was only 848px — so boards crept along for the sake of ~30px.
     Trimming the padding here (top still clears the top bar, bottom still clears
     the tab switcher) buys back ~40px, enough for the common board heights to
     sit perfectly still. Genuinely tall boards still scroll. */
  return (
    <Box sx={{ position: "absolute", inset: "7.4vh 2.2vw 7vh", fontFamily: FONT }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.8vw", mb: "1.2vh" }}>
        <Box component="span" sx={{ fontSize: "2.4vh", fontWeight: 600, letterSpacing: "-0.01em", color: dark ? "#F5F7FB" : "#0B1320" }}>
          {dashboard.name}
        </Box>
        <Box component="span" sx={{ fontSize: "1.3vh", fontWeight: 500, color: dark ? "rgba(245,247,251,0.45)" : "rgba(11,19,32,0.45)" }}>
          {workspaceName ? `${workspaceName} · ` : ""}{periodTitle}
        </Box>
      </Box>
      <Box
        ref={stageRef}
        sx={{
          position: "absolute",
          inset: "3.6vh 0 0",
          overflow: "hidden",
          // Initial fade heights (0 = no fade). The scroll loop overrides these
          // as inline styles; keeping them in `sx` (a class) means a metrics
          // re-render never resets them mid-scroll.
          "--tv-fade-top": "0px",
          "--tv-fade-bot": "0px",
          // Soft edges that only appear where content is actually scrolling off:
          // the fade heights are driven from the scroll position (see the loop),
          // so the top card is never faded while parked at the top.
          WebkitMaskImage:
            "linear-gradient(180deg, transparent 0, #000 var(--tv-fade-top), #000 calc(100% - var(--tv-fade-bot)), transparent 100%)",
          maskImage:
            "linear-gradient(180deg, transparent 0, #000 var(--tv-fade-top), #000 calc(100% - var(--tv-fade-bot)), transparent 100%)",
        }}
      >
        <Box
          ref={boardRef}
          className={`db-root tv-board${dark ? " tv-dark" : ""}`}
          sx={{
            width: "100%",
            willChange: "transform",
          }}
        >
          <Grid
            className="db-canvas"
            layout={widgets.map((w) => ({ i: w.i, x: w.x || 0, y: w.y || 0, w: w.w || 3, h: w.h || 3, static: true }))}
            cols={12}
            rowHeight={40}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={false}
            isResizable={false}
            useCSSTransforms
          >
            {widgets.map((w) => (
              <div key={w.i}>
                {w.kind === "component" ? (
                  <ComponentCard
                    widget={w}
                    period={boardWindow}
                    boardWindow={boardWindow}
                    boardCustom={boardCustom}
                    boardWidgets={boardWidgets}
                    editing={false}
                    onDuplicate={() => {}}
                    onRemove={() => {}}
                  />
                ) : (
                  <WidgetCard
                    widget={w}
                    metric={getMetric(w.metric)}
                    series={getSeries(w.metric, w.options?.granularity)}
                    summary={getSummary(w.metric)}
                    api={metricsApi}
                    editing={false}
                    onChangeType={() => {}}
                    onEdit={() => {}}
                    onDuplicate={() => {}}
                    onRemove={() => {}}
                  />
                )}
              </div>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
});

TVDashboardScene.propTypes = {
  dashboard: PropTypes.object.isRequired,
  dark: PropTypes.bool,
  workspaceName: PropTypes.string,
  navActive: PropTypes.bool,
};

export default TVDashboardScene;
